/**
 * Print Calculator — client-side logic for the App Block.
 *
 * Flow per file selection:
 *   0. Validate (type + size) → show preview + DPI badge
 *   1. POST /api/upload-init          → { upload_url, file_uuid }
 *   2. PUT  upload_url (Supabase)     → (no body)
 *   3. POST /api/parse-dimensions     → { width_cm, height_cm, source }
 *   4. POST /api/sign-price           → { price_stotinki, price_signature }
 *   5. Inject print properties into the cart via AJAX interception + hidden inputs
 *
 * Formula (mirrors pricing.server.ts — keep in sync):
 *   price = basePrice + width_cm * height_cm * ratePerCm2
 *   (minPrice sets a hard minimum quantity: ceil(minPrice / unitPrice))
 */

(function () {
  'use strict';

  // ── Constants ──────────────────────────────────────────────────────────────
  // Must match MAX_UPLOAD_BYTES and ALLOWED_EXTENSIONS in
  // app/lib/upload-policy.server.ts. This file used to allow 50 MB while the
  // server refused to read anything over 20 MB, so larger files uploaded fine
  // and then failed to produce a price.
  var MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB
  var ALLOWED_TYPES = {
    'image/jpeg': true,
    'image/png': true,
    'image/tiff': true,
    'application/pdf': true,
  };
  var ALLOWED_EXT = /\.(jpe?g|png|tiff?|pdf)$/i;

  // ── Money ──────────────────────────────────────────────────────────────────
  // The shop's real currency and locale, handed over by the block. Prices used
  // to be printed as "лв" unconditionally while checkout charged in whatever
  // currency the shop actually uses, so a euro store showed the buyer one
  // currency and billed another.

  var moneyCurrency = 'EUR';
  var moneyLocale;
  var moneyFormatter = null;

  function configureMoney(currency, locale) {
    if (currency) moneyCurrency = currency;
    if (locale) moneyLocale = locale;
    moneyFormatter = null;
  }

  function formatMoney(stotinki) {
    var amount = Number(stotinki) / 100;
    try {
      if (!moneyFormatter) {
        moneyFormatter = new Intl.NumberFormat(moneyLocale || undefined, {
          style: 'currency',
          currency: moneyCurrency,
        });
      }
      return moneyFormatter.format(amount);
    } catch (e) {
      // Unknown currency or locale: still show a sane number rather than NaN.
      return amount.toFixed(2) + ' ' + moneyCurrency;
    }
  }

  // ── Shared state — current print properties (one block at a time) ─────────
  // This is picked up by the fetch/XHR interceptors below.
  var activePrintProperties = null;

  // Save a reference to the native fetch BEFORE we monkey-patch it.
  // Our own API calls (get/post helpers) use this to bypass the interceptor.
  var nativeFetch = window.fetch.bind(window);

  // Run once DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    var blocks = document.querySelectorAll(
      '.pc-premium-block[data-app-url][data-product-id], .pc-block[data-app-url][data-product-id]'
    );
    // Old deployed markup remains compatible during rollout. A draft-only
    // block must leave the theme's cart transport entirely untouched.
    if (blocks.length && blocks[0].dataset.printCart !== 'true') {
      installFetchInterceptor();
      installXHRInterceptor();
    }

    // A product has one design, one size and one price, and the properties this
    // produces are attached to whatever goes into the cart. Two blocks would
    // overwrite each other's upload, so the extras are hidden rather than left
    // to fight — and the merchant is told, because the duplicate belongs in the
    // theme editor's bin.
    if (blocks.length > 1) {
      console.warn(
        '[print-calculator] ' + blocks.length + ' calculator blocks on this page; ' +
          'using the first and hiding the rest. Remove the duplicates in the theme editor.'
      );
    }

    blocks.forEach(function (block, index) {
      if (index === 0) initBlock(block);
      else block.style.display = 'none';
    });
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  FETCH & XHR Interceptors
  //  Modern Shopify themes (Dawn etc.) add to cart via fetch('/cart/add.js').
  //  We intercept those calls and inject our _print_* line item properties
  //  so they're included without relying on hidden form inputs.
  // ══════════════════════════════════════════════════════════════════════════

  function installFetchInterceptor() {
    var originalFetch = window.fetch;
    window.fetch = function (resource, options) {
      // Determine the URL string
      var url = '';
      if (typeof resource === 'string') {
        url = resource;
      } else if (resource && resource.url) {
        url = resource.url;
      }

      if (activePrintProperties && isCartAddUrl(url)) {
        try {
          if (options && options.body) {
            // Most common path: fetch(url, { method: 'POST', body: ... })
            options = injectIntoFetchOptions(url, options, activePrintProperties);
          } else if (!options && resource instanceof Request) {
            // Some themes pass a Request object: fetch(new Request('/cart/add.js', { body: ... }))
            // We can't easily modify a Request, so we reconstruct it
            return resource.text().then(function (bodyText) {
              var newBody = injectIntoStringBody(bodyText, activePrintProperties);
              var newOpts = {
                method: resource.method,
                headers: resource.headers,
                body: newBody,
              };
              return originalFetch.call(this, url, newOpts);
            }.bind(this));
          }
        } catch (e) {
          console.warn('[print-calculator] failed to inject properties into fetch:', e);
        }
      }

      return originalFetch.call(this, resource, options);
    };
  }

  function installXHRInterceptor() {
    var originalOpen = XMLHttpRequest.prototype.open;
    var originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this._pcUrl = url;
      this._pcMethod = method;
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function (body) {
      if (activePrintProperties && isCartAddUrl(this._pcUrl)) {
        try {
          body = injectIntoXHRBody(body, activePrintProperties);
        } catch (e) {
          console.warn('[print-calculator] failed to inject properties into XHR:', e);
        }
      }
      return originalSend.call(this, body);
    };
  }

  function isCartAddUrl(url) {
    if (!url) return false;
    // Match /cart/add, /cart/add.js, /cart/add.json etc.
    return /\/cart\/add(\.js|\.json)?\s*$/i.test(url.split('?')[0]);
  }

  /**
   * Helper to recursively inject print properties and custom ID into cart JSON body items
   */
  function injectPropertiesIntoJson(json, props) {
    var customId = props._custom_variant_id;
    var customQuantity = props._custom_quantity;
    if (!json) return json;
    if (json.items && Array.isArray(json.items)) {
      json.items.forEach(function (item) {
        if (customId) item.id = customId;
        if (customQuantity) item.quantity = parseInt(customQuantity, 10);
        item.properties = item.properties || {};
        for (var key in props) {
          if (Object.prototype.hasOwnProperty.call(props, key) && key !== '_custom_variant_id' && key !== '_custom_quantity') {
            item.properties[key] = props[key];
          }
        }
      });
    } else {
      if (customId) json.id = customId;
      if (customQuantity) json.quantity = parseInt(customQuantity, 10);
      json.properties = json.properties || {};
      for (var key in props) {
        if (Object.prototype.hasOwnProperty.call(props, key) && key !== '_custom_variant_id' && key !== '_custom_quantity') {
          json.properties[key] = props[key];
        }
      }
    }
    return json;
  }

  /**
   * Always force the block quantity onto cart/add payloads.
   * Themes that hide/remove the quantity field omit `quantity`, and older logic
   * only overwrote it when already present — so Add to cart fell back to 1.
   */
  function applyCustomQuantity(target, props) {
    if (!target || !props || !props._custom_quantity) return;
    var qty = String(props._custom_quantity);
    if (typeof target.set !== 'function') return;

    if (target.has('items[0][id]') || target.has('items[0][quantity]')) {
      target.set('items[0][quantity]', qty);
    }
    target.set('quantity', qty);
  }

  /**
   * Inject print properties into fetch options.
   * Handles: JSON body, FormData body, URLSearchParams body
   */
  function injectIntoFetchOptions(url, options, props) {
    if (!options) return options;
    var body = options.body;
    if (!body) return options;

    // Clone options so we don't mutate the caller's object
    var newOptions = {};
    for (var k in options) {
      if (Object.prototype.hasOwnProperty.call(options, k)) newOptions[k] = options[k];
    }

    // ── JSON body (most common in modern themes) ───────────────────────────
    if (typeof body === 'string') {
      try {
        var json = JSON.parse(body);
        json = injectPropertiesIntoJson(json, props);
        newOptions.body = JSON.stringify(json);
        return newOptions;
      } catch (e) {
        // Not JSON, try URL-encoded
        if (body.indexOf('=') > -1) {
          var params = new URLSearchParams(body);
          if (props._custom_variant_id) {
             if (params.has('id')) params.set('id', props._custom_variant_id);
             if (params.has('items[0][id]')) params.set('items[0][id]', props._custom_variant_id);
          }
          applyCustomQuantity(params, props);
          for (var pKey in props) {
            if (Object.prototype.hasOwnProperty.call(props, pKey) && pKey !== '_custom_variant_id' && pKey !== '_custom_quantity') {
              params.set('properties[' + pKey + ']', props[pKey]);
            }
          }
          newOptions.body = params.toString();
          return newOptions;
        }
      }
    }

    // ── FormData body ──────────────────────────────────────────────────────
    if (body instanceof FormData) {
      if (props._custom_variant_id) {
        if (body.has('id')) body.set('id', props._custom_variant_id);
        if (body.has('items[0][id]')) body.set('items[0][id]', props._custom_variant_id);
      }
      applyCustomQuantity(body, props);
      for (var fdKey in props) {
        if (
          Object.prototype.hasOwnProperty.call(props, fdKey) &&
          fdKey !== '_custom_variant_id' &&
          fdKey !== '_custom_quantity'
        ) {
          body.set('properties[' + fdKey + ']', props[fdKey]);
        }
      }
      return newOptions;
    }

    // ── URLSearchParams body ───────────────────────────────────────────────
    if (body instanceof URLSearchParams) {
      if (props._custom_variant_id) {
        if (body.has('id')) body.set('id', props._custom_variant_id);
        if (body.has('items[0][id]')) body.set('items[0][id]', props._custom_variant_id);
      }
      applyCustomQuantity(body, props);
      for (var uspKey in props) {
        if (Object.prototype.hasOwnProperty.call(props, uspKey) && uspKey !== '_custom_variant_id' && uspKey !== '_custom_quantity') {
          body.set('properties[' + uspKey + ']', props[uspKey]);
        }
      }
      return newOptions;
    }

    return newOptions;
  }

  /**
   * Inject print properties into XHR body.
   * Returns modified body.
   */
  function injectIntoXHRBody(body, props) {
    if (!body) return body;

    // JSON string
    if (typeof body === 'string') {
      try {
        var json = JSON.parse(body);
        json = injectPropertiesIntoJson(json, props);
        return JSON.stringify(json);
      } catch (e) {
        // Not JSON, try URL-encoded
        if (body.indexOf('=') > -1) {
          var params = new URLSearchParams(body);
          if (props._custom_variant_id) {
             if (params.has('id')) params.set('id', props._custom_variant_id);
             if (params.has('items[0][id]')) params.set('items[0][id]', props._custom_variant_id);
          }
          applyCustomQuantity(params, props);
          for (var pKey in props) {
            if (Object.prototype.hasOwnProperty.call(props, pKey) && pKey !== '_custom_variant_id' && pKey !== '_custom_quantity') {
              params.set('properties[' + pKey + ']', props[pKey]);
            }
          }
          return params.toString();
        }
      }
    }

    // FormData
    if (body instanceof FormData) {
      if (props._custom_variant_id) {
        if (body.has('id')) body.set('id', props._custom_variant_id);
        if (body.has('items[0][id]')) body.set('items[0][id]', props._custom_variant_id);
      }
      applyCustomQuantity(body, props);
      for (var fdKey in props) {
        if (Object.prototype.hasOwnProperty.call(props, fdKey) && fdKey !== '_custom_variant_id' && fdKey !== '_custom_quantity') {
          body.set('properties[' + fdKey + ']', props[fdKey]);
        }
      }
    }

    return body;
  }

  /**
   * Inject print properties into a string body (JSON or URL-encoded).
   * Used when reconstructing a Request object.
   */
  function injectIntoStringBody(bodyText, props) {
    if (!bodyText) return bodyText;
    // Try JSON first
    try {
      var json = JSON.parse(bodyText);
      json = injectPropertiesIntoJson(json, props);
      return JSON.stringify(json);
    } catch (e) {
      // Try URL-encoded
      if (bodyText.indexOf('=') > -1) {
        var params = new URLSearchParams(bodyText);
        if (props._custom_variant_id) {
           if (params.has('id')) params.set('id', props._custom_variant_id);
           if (params.has('items[0][id]')) params.set('items[0][id]', props._custom_variant_id);
        }
        applyCustomQuantity(params, props);
        for (var pKey in props) {
          if (Object.prototype.hasOwnProperty.call(props, pKey) && pKey !== '_custom_variant_id' && pKey !== '_custom_quantity') {
            params.set('properties[' + pKey + ']', props[pKey]);
          }
        }
        return params.toString();
      }
    }
    return bodyText;
  }

  // ══════════════════════════════════════════════════════════════════════════

  function initBlock(el) {
    var appUrl = (el.dataset.appUrl || '')
      .replace(/^[\s\xa0]+|[\s\xa0]+$/g, '')
      .replace(/^https?:\/+(?!\/)/, function (match) {
        return match.toLowerCase().indexOf('https') !== -1 ? 'https://' : 'http://';
      })
      .replace(/\/$/, '');
    var shop = el.dataset.shop || '';
    var productId = el.dataset.productId || '';

    if (!appUrl) {
      el.style.display = 'none';
      console.warn('[print-calculator] app_url block setting is not set');
      return;
    }

    var statusEl = el.querySelector('[data-status]');
    var priceEl = el.querySelector('[data-price]');
    var priceRowEl = el.querySelector('[data-price-row]');
    var qtyInput = document.querySelector('input[name="quantity"]') || document.querySelector('[name="quantity"]');
    var fileInput = el.querySelector('input[type="file"]');
    var dropzone = el.querySelector('[data-dropzone]');
    var validationEl = el.querySelector('[data-validation]');
    var previewEl = el.querySelector('[data-preview]');
    var previewThumb = el.querySelector('[data-preview-thumb]');
    var previewName = el.querySelector('[data-preview-name]');
    var previewSize = el.querySelector('[data-preview-size]');
    var previewRemove = el.querySelector('[data-preview-remove]');
    var dpiEl = el.querySelector('[data-dpi]');
    var sizeEl = el.querySelector('[data-size]');
    var widthInput = el.querySelector('[data-width]');
    var heightInput = el.querySelector('[data-height]');
    var sizeNoteEl = el.querySelector('[data-size-note]');
    var orderEl = el.querySelector('[data-order]');
    var unitPriceEl = el.querySelector('[data-unit-price]');
    var totalPriceEl = el.querySelector('[data-total-price]');
    var qtyInputBlock = el.querySelector('[data-qty]');
    var qtyNoteEl = el.querySelector('[data-qty-note]');

    // The design being priced, and the shape it has to keep.
    var currentFileUuid = null;
    var operationVersion = 0;
    var aspectRatio = null;
    var sizeBounds = { minW: 1, maxW: 1000, minH: 1, maxH: 1000 };
    // Usable roll/sheet width for this product; null = no media-specific limit.
    var maxMediaWidthCm = null;
    var minPriceStotinki = 0;
    var minQuantity = 1;
    var unitPriceStotinki = 0;
    // Display state belongs to this block, independently of Shopify cart injection.
    var displayPrintProperties = null;
    var resizeTimer = null;
    var syncingSize = false;

    // Track objectURL for cleanup
    var currentObjectUrl = null;

    // Track original prices for restoration
    var originalPrices = [];
    var priceElements = [];
    var syncedPrice = null;
    var priceObserver = null;
    var priceObserverTimer = null;
    // Caps how often we fight a theme money-format rewrite for one price.
    // Real section morphs need one re-apply; an unbounded fight freezes the tab.
    var priceReapplyBudget = 0;
    // Merchants who would rather leave the theme price alone can turn this off.
    var syncThemePrice = el.dataset.printCart !== 'true' && el.dataset.syncThemePrice !== 'false';

    configureMoney(el.dataset.currency, el.dataset.locale);

    function findThemePriceElements() {
      var selectors = [
        '.price-item--regular',
        '.price-item--sale',
        '.price-item',
        '.product-single__price',
        '.product__price .price-item',
        '[data-product-price]',
        '.product-price',
        '.current-price',
        '.price .price-item'
      ];
      var elements = [];
      selectors.forEach(function (sel) {
        document.querySelectorAll(sel).forEach(function (element) {
          if (elements.indexOf(element) === -1) {
            elements.push(element);
          }
        });
      });
      return elements;
    }

    function captureOriginalPrices() {
      priceElements = findThemePriceElements();
      originalPrices = priceElements.map(function (element) {
        return element.textContent || '';
      });
    }

    /**
     * Keeps the product page showing the calculated unit price (per piece).
     *
     * Order total stays in the calculator block. Syncing the total into the
     * theme while also syncing quantity made Add to cart look like
     * total × qty. Shopify always multiplies catalogue/unit × quantity.
     *
     * Themes re-render their price node on variant change and on section
     * morphing, which silently reverted this, so the value is re-applied when
     * that happens.
     */
    function updateThemePrices(priceStotinki) {
      if (!syncThemePrice) return;
      if (priceElements.length === 0) {
        captureOriginalPrices();
      }

      var formatted = formatMoney(priceStotinki);
      syncedPrice = formatted;
      priceReapplyBudget = 5;

      priceElements.forEach(function (element) {
        writePrice(element, formatted);
      });

      watchForThemeRerender();
    }

    function writePrice(element, text) {
      // No-op writes still replace the text node and re-fire the observer.
      if (element.textContent === text) return;

      // Disconnect before writing: MutationObserver callbacks are microtasks, so
      // a synchronous isWritingPrice flag is already cleared by the time they
      // run and cannot guard against our own writes.
      if (priceObserver) priceObserver.disconnect();
      element.textContent = text;
      if (priceObserver) {
        priceObserver.takeRecords();
        observePriceElements();
      }
    }

    function observePriceElements() {
      if (!priceObserver) return;
      priceElements.forEach(function (element) {
        if (element.parentNode) {
          priceObserver.observe(element.parentNode, {
            childList: true,
            characterData: true,
            subtree: true,
          });
        }
      });
    }

    function watchForThemeRerender() {
      if (priceObserver || priceElements.length === 0) return;

      priceObserver = new MutationObserver(function () {
        if (!syncedPrice || priceReapplyBudget <= 0) return;
        // Defer to a macrotask so a theme money-format rewrite cannot spin the
        // microtask queue into a "Page Unresponsive" freeze.
        if (priceObserverTimer) return;
        priceObserverTimer = setTimeout(function () {
          priceObserverTimer = null;
          if (!syncedPrice || priceReapplyBudget <= 0) return;
          var needsWrite = priceElements.some(function (element) {
            return element.isConnected && element.textContent !== syncedPrice;
          });
          if (!needsWrite) return;
          priceReapplyBudget -= 1;
          priceElements.forEach(function (element) {
            if (element.isConnected && element.textContent !== syncedPrice) {
              writePrice(element, syncedPrice);
            }
          });
        }, 0);
      });

      observePriceElements();
    }

    function restoreThemePrices() {
      syncedPrice = null;
      priceReapplyBudget = 0;
      if (priceObserverTimer) {
        clearTimeout(priceObserverTimer);
        priceObserverTimer = null;
      }
      if (priceObserver) {
        priceObserver.disconnect();
        priceObserver = null;
      }
      priceElements.forEach(function (element, idx) {
        if (originalPrices[idx] !== undefined) {
          writePrice(element, originalPrices[idx]);
        }
      });
    }

    // ── 1. Fetch pricing config — hide block if product not configured ──────
    get(appUrl + '/api/pricing-config?shop=' + enc(shop) + '&product_id=' + enc(productId))
      .then(function (config) {
        if (!config.enabled) el.style.display = 'none';
      })
      .catch(function () {
        // Network / CORS failures used to hide the whole block after a one-second
        // flash. Leave it visible; upload and price calls still fail closed.
        console.warn('[print-calculator] could not load pricing config');
      });

    // ── Drag & Drop visual ──────────────────────────────────────────────────
    if (dropzone) {
      dropzone.addEventListener('dragover', function (e) {
        e.preventDefault();
        dropzone.classList.add('pc-dragover');
      });
      dropzone.addEventListener('dragleave', function () {
        dropzone.classList.remove('pc-dragover');
      });
      dropzone.addEventListener('drop', function () {
        dropzone.classList.remove('pc-dragover');
      });
    }

    // ── Remove button ───────────────────────────────────────────────────────
    if (previewRemove) {
      previewRemove.addEventListener('click', function () {
        resetUI();
        fileInput.value = '';
      });
    }

    // ── Form submit interception ────────────────────────────────────────────
    // Also intercept the submit event on the product form to inject properties
    // into FormData — covers themes that use traditional form POST.
    var productForm = findProductForm(el);
    if (productForm) {
      var formQty = productForm.querySelector('input[name="quantity"]') || productForm.querySelector('[name="quantity"]');
      if (formQty) qtyInput = formQty;
      productForm.addEventListener('submit', function (ev) {
        if (!activePrintProperties) return;

        var qty = clampQty(
          parseInt(
            (qtyInputBlock && qtyInputBlock.value) ||
              (qtyInput && qtyInput.value) ||
              '1',
            10
          )
        );
        if (getBlockQty() < requiredMinQty()) {
          ev.preventDefault();
          syncQuantityToCart(qty);
          setStatus(
            statusEl,
            'Минималната поръчка е ' + requiredMinQty() + ' бр.',
            true
          );
          return;
        }
        syncQuantityToCart(qty);

        // Swap the main ID input if it exists
        if (activePrintProperties._custom_variant_id) {
           var idInput = productForm.querySelector('input[name="id"]');
           if (idInput) {
             idInput.value = activePrintProperties._custom_variant_id;
           } else {
             setHidden(productForm, 'id', activePrintProperties._custom_variant_id);
           }
        }

        // Ensure quantity is on the form even when the theme removed the control.
        setHidden(productForm, 'quantity', String(qty));

        // Inject hidden inputs right before submission (redundant safety net)
        for (var key in activePrintProperties) {
          if (Object.prototype.hasOwnProperty.call(activePrintProperties, key) && key !== '_custom_variant_id' && key !== '_custom_quantity') {
            setHidden(productForm, 'properties[' + key + ']', activePrintProperties[key]);
          }
        }
      });
    }

    // ── 2. File selection ───────────────────────────────────────────────────
    fileInput.addEventListener('change', function (e) {
      var file = e.target.files && e.target.files[0];
      resetUI();
      if (!file) return;
      var selectionVersion = operationVersion;
      function assertSelectionCurrent() {
        if (selectionVersion !== operationVersion) throw new Error('Superseded file selection');
      }

      // ── Quality Gate: Validate ──────────────────────────────────────────
      var errors = validateFile(file);
      if (errors.length > 0) {
        showValidation(errors.join(' '));
        fileInput.value = '';
        return;
      }

      // ── Show Preview ────────────────────────────────────────────────────
      showPreview(file);

      // ── Start Upload Pipeline ───────────────────────────────────────────
      setStatus(statusEl, 'Подготовка за качване\u2026', false, true);
      priceEl.style.display = 'none';
      clearPrintProperties(el);

      // Step 1: upload-init
      post(appUrl + '/api/upload-init', {
        shop: shop,
        product_id: productId,
        filename: file.name,
        size: file.size,
      })
        .then(function (init) {
          assertSelectionCurrent();
          if (!init.upload_url || !init.file_uuid) throw new Error('Invalid upload-init response');
          var fileUuid = init.file_uuid;

          // Step 2: PUT to Supabase with progress tracking
          setStatus(statusEl, 'Качване\u2026', false, true);
          return uploadWithProgress(init.upload_url, file, function (pct) {
            if (selectionVersion === operationVersion) setStatus(statusEl, 'Качване\u2026 ' + pct + '%', false, true);
          })
            .then(function () {
              assertSelectionCurrent();
              setStatus(statusEl, 'Проверка на файла\u2026', false, true);
              return fileUuid;
            });
        })
        .then(function (fileUuid) {
          assertSelectionCurrent();
          // Step 3: measure. The server decides whether the file states a size
          // it can be priced from, or whether it is only a suggestion.
          return post(appUrl + '/api/parse-dimensions', {
            shop: shop,
            product_id: productId,
            file_uuid: fileUuid,
          })
            .then(function (dim) {
              assertSelectionCurrent();
              currentFileUuid = fileUuid;
              aspectRatio = dim.aspect_ratio || null;
              maxMediaWidthCm =
                dim.max_media_width_cm > 0 ? dim.max_media_width_cm : null;
              sizeBounds = {
                minW: dim.min_width_cm || 1,
                maxW: dim.max_width_cm || 1000,
                minH: dim.min_height_cm || 1,
                maxH: dim.max_height_cm || 1000,
              };
              showSizeControl(dim);
              // File DPI badge is locked here — resizing must not change it.
              showQuality(dim.quality);
              return fileUuid;
            });
        })
        .then(function (fileUuid) {
          assertSelectionCurrent();
          // Step 4: price the stored size; buyer can later resize by either side.
          setStatus(statusEl, 'Изчисляване на цената\u2026', false, true);
          return priceFor(fileUuid, null);
        })
        .then(applyPricing)
        .catch(function (err) {
          if (selectionVersion !== operationVersion) return;
          setStatus(statusEl, 'Грешка: ' + (err.message || 'Нещо се обърка'), true);
        });
    });

    // ── Size ────────────────────────────────────────────────────────────────

    /**
     * Reveals width and height inputs once the file has been measured.
     * Editing either side keeps the artwork aspect ratio; the note explains
     * where the size came from and any media-width limit.
     */
    function showSizeControl(dim) {
      if (!sizeEl || !widthInput || !heightInput) return;

      widthInput.min = String(sizeBounds.minW);
      widthInput.max = String(sizeBounds.maxW);
      heightInput.min = String(sizeBounds.minH);
      heightInput.max = String(sizeBounds.maxH);
      syncingSize = true;
      widthInput.value = String(dim.width_cm);
      heightInput.value = String(dim.height_cm);
      syncingSize = false;

      if (sizeNoteEl) {
        var note = dim.size_is_trusted
          ? 'Размерът е разчетен от вашия файл. Можете да го промените.'
          : '';
        if (dim.size_clamped_to_media && maxMediaWidthCm) {
          note =
            'Размерът е намален, за да се събере в медията (макс. по-къса страна ' +
            maxMediaWidthCm +
            ' см).';
        } else if (note && maxMediaWidthCm) {
          note +=
            ' По-късата страна може да е най-много ' + maxMediaWidthCm + ' см.';
        }
        sizeNoteEl.textContent = note;
        sizeNoteEl.style.display = note ? '' : 'none';
        sizeNoteEl.classList.toggle(
          'pc-size-note--check',
          !dim.size_is_trusted || !!dim.size_clamped_to_media
        );
      }

      sizeEl.style.display = 'block';
    }

    function setSizeInputs(widthCm, heightCm) {
      if (!widthInput || !heightInput) return;
      syncingSize = true;
      widthInput.value = String(widthCm);
      heightInput.value = String(heightCm);
      syncingSize = false;
    }

    /**
     * Prices the current file. Pass `{ widthCm }` or `{ heightCm }` to resize;
     * omit both to price the stored ordered size.
     */
    function priceFor(fileUuid, resize) {
      var pricingVersion = operationVersion;
      if (resize && el.dataset.printCart === 'true') {
        el.dispatchEvent(new CustomEvent('print:invalidate', { detail: fileUuid }));
      }
      var payload = { shop: shop, product_id: productId, file_uuid: fileUuid };
      if (resize && resize.widthCm != null) payload.width_cm = resize.widthCm;
      if (resize && resize.heightCm != null) payload.height_cm = resize.heightCm;

      return post(appUrl + '/api/sign-price', payload).then(function (res) {
        if (pricingVersion !== operationVersion || currentFileUuid !== fileUuid) {
          throw new Error('Superseded print price');
        }
        if (!res.print_attributes) throw new Error('Invalid sign-price response');

        setSizeInputs(res.width_cm, res.height_cm);
        unitPriceStotinki = res.price_stotinki;
        minPriceStotinki = res.min_price_stotinki || 0;
        minQuantity = res.min_quantity || 1;

        return {
          operationVersion: pricingVersion,
          fileUuid: fileUuid,
          widthCm: res.width_cm,
          heightCm: res.height_cm,
          priceStotinki: res.price_stotinki,
          printAttributes: res.print_attributes,
        };
      });
    }

    function applyPricing(result) {
      if (result.operationVersion !== operationVersion || result.fileUuid !== currentFileUuid) return;
      displayPrintProperties = result.printAttributes;
      setPrintProperties(el, result);
      var qty = clampQty(getBlockQty());
      if (qtyInputBlock) {
        qtyInputBlock.min = String(requiredMinQty());
        qtyInputBlock.value = String(qty);
      }
      syncQuantityToCart(qty);
      renderPriceDisplay();

      if (orderEl) orderEl.style.display = 'flex';
      if (priceRowEl) priceRowEl.style.display = 'flex';
      else if (priceEl) priceEl.style.display = '';
      setStatus(statusEl, '', false);
    }

    function getBlockQty() {
      if (!qtyInputBlock) return 1;
      var q = parseInt(qtyInputBlock.value, 10);
      return isFinite(q) && q >= 1 ? q : 1;
    }

    /** Hard minimum pieces so qty × unit covers the order minimum. */
    function requiredMinQty() {
      return minQuantity > 1 ? minQuantity : 1;
    }

    function clampQty(qty) {
      var q = isFinite(qty) && qty >= 1 ? Math.floor(qty) : 1;
      return Math.max(q, requiredMinQty());
    }

    /**
     * Pushes the block quantity into Add to cart (`_custom_quantity` + hidden
     * theme input) without showing the theme's quantity stepper. Buyers pick
     * pieces only in this block; seeing 40 in both places looked like a double
     * charge.
     */
    var hiddenThemeQty = [];

    function themeQuantityRoots(form) {
      if (!form) return [];
      var roots = [];
      var seen = [];

      function add(node) {
        if (!node || seen.indexOf(node) !== -1) return;
        seen.push(node);
        roots.push(node);
      }

      var qtyField =
        form.querySelector('input[name="quantity"]') ||
        form.querySelector('[name="quantity"]');
      if (qtyField) {
        add(
          qtyField.closest('quantity-input') ||
            qtyField.closest('.quantity') ||
            qtyField.closest('.product-form__quantity') ||
            qtyField.closest('[class*="quantity"]') ||
            qtyField.parentElement
        );
      }

      form.querySelectorAll(
        'quantity-input, .quantity, .product-form__quantity, .quantity-selector, [data-quantity-wrapper]'
      ).forEach(add);

      return roots.filter(Boolean);
    }

    function hideThemeQuantity() {
      if (el.dataset.printCart === 'true') return;
      var form = findProductForm(el);
      var roots = themeQuantityRoots(form);
      if (roots.length === 0 && qtyInput) {
        roots = themeQuantityRoots(qtyInput.form || qtyInput.closest('form'));
        if (roots.length === 0) {
          roots = [
            qtyInput.closest('quantity-input') ||
              qtyInput.closest('.quantity') ||
              qtyInput.parentElement,
          ].filter(Boolean);
        }
      }

      roots.forEach(function (node) {
        if (!node || node.getAttribute('data-pc-qty-hidden') === '1') return;
        hiddenThemeQty.push({
          element: node,
          display: node.style.display,
        });
        node.setAttribute('data-pc-qty-hidden', '1');
        node.style.display = 'none';
      });
    }

    function showThemeQuantity() {
      if (el.dataset.printCart === 'true') return;
      hiddenThemeQty.forEach(function (entry) {
        entry.element.style.display = entry.display;
        entry.element.removeAttribute('data-pc-qty-hidden');
      });
      hiddenThemeQty = [];
      document.querySelectorAll('[data-pc-qty-hidden="1"]').forEach(function (node) {
        node.style.display = '';
        node.removeAttribute('data-pc-qty-hidden');
      });
    }

    function syncQuantityToCart(qty) {
      qty = clampQty(qty);
      if (qtyInputBlock && String(qtyInputBlock.value) !== String(qty)) {
        qtyInputBlock.value = String(qty);
      }
      if (el.dataset.printCart === 'true') return;
      if (activePrintProperties) {
        activePrintProperties._custom_quantity = String(qty);
      }
      var form = findProductForm(el);
      // Theme may hide/remove the visible quantity control — keep a real
      // quantity field so form POST and AJAX serializers still send the count.
      var themeQty = null;
      if (form) {
        themeQty =
          form.querySelector('input[name="quantity"]') ||
          form.querySelector('[name="quantity"]');
        if (!themeQty) {
          themeQty = document.createElement('input');
          themeQty.type = 'hidden';
          themeQty.name = 'quantity';
          themeQty.setAttribute('data-pc-qty-injected', '1');
          form.appendChild(themeQty);
        }
        themeQty.value = String(qty);
      }
      if (qtyInput) qtyInput.value = String(qty);
      else if (themeQty) qtyInput = themeQty;

      hideThemeQuantity();
    }

    function updateQtyHint() {
      if (!qtyNoteEl) return;
      if (minQuantity > 1 && minPriceStotinki > 0) {
        qtyNoteEl.textContent =
          'Минимална поръчка: поне ' +
          minQuantity +
          ' бр. (от ' +
          formatMoney(minPriceStotinki) +
          ').';
        qtyNoteEl.classList.add('pc-size-note--check');
      } else {
        qtyNoteEl.textContent = '';
        qtyNoteEl.classList.remove('pc-size-note--check');
      }
    }

    function shorterSideExceedsMedia(widthCm, heightCm) {
      if (!maxMediaWidthCm) return false;
      return Math.min(widthCm, heightCm) > maxMediaWidthCm + 1e-9;
    }

    function scheduleResize(resize) {
      if (!currentFileUuid) return;
      var resizeVersion = operationVersion;
      var resizingFileUuid = currentFileUuid;
      clearPrintProperties(el);
      if (orderEl) orderEl.style.display = 'none';

      setStatus(statusEl, 'Преизчисляване\u2026', false, true);
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (resizeVersion !== operationVersion || resizingFileUuid !== currentFileUuid) return;
        priceFor(resizingFileUuid, resize)
          .then(applyPricing)
          .catch(function (err) {
            if (resizeVersion !== operationVersion) return;
            setStatus(statusEl, 'Грешка: ' + (err.message || 'Нещо се обърка'), true);
          });
      }, 400);
    }

    function onWidthChanged() {
      if (!currentFileUuid || syncingSize) return;
      operationVersion++;

      var requested = parseFloat(widthInput.value);
      if (!isFinite(requested) || requested <= 0) return;

      var heightCm = aspectRatio
        ? Math.round((requested / aspectRatio) * 10) / 10
        : parseFloat(heightInput.value);
      if (aspectRatio) {
        syncingSize = true;
        heightInput.value = String(heightCm);
        syncingSize = false;
      }

      if (
        requested > sizeBounds.maxW ||
        shorterSideExceedsMedia(requested, heightCm)
      ) {
        clearTimeout(resizeTimer);
        clearPrintProperties(el);
        setStatus(
          statusEl,
          maxMediaWidthCm
            ? 'Максималната по-къса страна за този материал е ' + maxMediaWidthCm + ' см.'
            : 'Размерът е твърде голям.',
          true
        );
        return;
      }

      scheduleResize({ widthCm: requested });
    }

    function onHeightChanged() {
      if (!currentFileUuid || syncingSize) return;
      operationVersion++;

      var requested = parseFloat(heightInput.value);
      if (!isFinite(requested) || requested <= 0) return;

      var widthCm = aspectRatio
        ? Math.round(requested * aspectRatio * 10) / 10
        : parseFloat(widthInput.value);
      if (aspectRatio) {
        syncingSize = true;
        widthInput.value = String(widthCm);
        syncingSize = false;
      }

      if (
        requested > sizeBounds.maxH ||
        shorterSideExceedsMedia(widthCm, requested)
      ) {
        clearTimeout(resizeTimer);
        clearPrintProperties(el);
        setStatus(
          statusEl,
          maxMediaWidthCm
            ? 'Максималната по-къса страна за този материал е ' + maxMediaWidthCm + ' см.'
            : 'Размерът е твърде голям.',
          true
        );
        return;
      }

      scheduleResize({ heightCm: requested });
    }

    if (widthInput) {
      widthInput.addEventListener('input', onWidthChanged);
      widthInput.addEventListener('change', onWidthChanged);
    }
    if (heightInput) {
      heightInput.addEventListener('input', onHeightChanged);
      heightInput.addEventListener('change', onHeightChanged);
    }
    if (qtyInputBlock) {
      qtyInputBlock.addEventListener('input', function () {
        var raw = parseInt(qtyInputBlock.value, 10);
        if (!isFinite(raw) || raw < 1) {
          renderPriceDisplay();
          return;
        }
        if (raw < requiredMinQty()) {
          // Keep the typed value visible while editing, but do not let a
          // below-minimum quantity reach the cart.
          updateQtyHint();
          setStatus(
            statusEl,
            'Минималната поръчка е ' + requiredMinQty() + ' бр.',
            true
          );
          if (activePrintProperties) {
            activePrintProperties._custom_quantity = String(requiredMinQty());
          }
          renderPriceDisplay();
          return;
        }
        setStatus(statusEl, '', false);
        syncQuantityToCart(raw);
        renderPriceDisplay();
      });
      qtyInputBlock.addEventListener('change', function () {
        var qty = clampQty(getBlockQty());
        qtyInputBlock.value = String(qty);
        setStatus(statusEl, '', false);
        syncQuantityToCart(qty);
        renderPriceDisplay();
      });
    }

    // ── Internal helpers ────────────────────────────────────────────────────

    function validateFile(file) {
      var errors = [];
      // Check type
      if (!ALLOWED_TYPES[file.type] && !ALLOWED_EXT.test(file.name)) {
        errors.push('❌ Неподдържан формат. Моля, използвайте JPG, PNG, TIFF или PDF.');
      }
      // Check size
      if (file.size > MAX_FILE_SIZE_BYTES) {
        var sizeMB = (file.size / 1024 / 1024).toFixed(1);
        var limitMB = Math.round(MAX_FILE_SIZE_BYTES / 1024 / 1024);
        errors.push(
          '❌ Файлът е твърде голям (' + sizeMB + ' MB). Максимално позволени: ' + limitMB + ' MB.'
        );
      }
      return errors;
    }

    function showValidation(msg) {
      if (!validationEl) return;
      validationEl.textContent = msg;
      validationEl.style.display = 'block';
    }

    function showPreview(file) {
      if (!previewEl) return;

      // Set file info
      previewName.textContent = file.name;
      previewSize.textContent = formatFileSize(file.size);

      // Thumbnail
      if (file.type === 'image/jpeg' || file.type === 'image/png') {
        currentObjectUrl = URL.createObjectURL(file);
        previewThumb.src = currentObjectUrl;
        previewThumb.style.display = '';
      } else {
        // PDF — show a generic icon
        previewThumb.src = 'data:image/svg+xml,' + encodeURIComponent(
          '<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">' +
          '<rect width="56" height="56" rx="8" fill="#e8e0f0"/>' +
          '<text x="28" y="34" text-anchor="middle" font-size="16" font-weight="bold" fill="#7b61a5">PDF</text>' +
          '</svg>'
        );
        previewThumb.style.display = '';
      }

      previewEl.style.display = 'flex';
    }


    /**
     * Shows the file's authored resolution. Locked at upload — enlarging the
     * print does not change this number, so buyers are not told their 300 DPI
     * file "became" 30 DPI.
     */
    function showQuality(quality) {
      if (!dpiEl) return;

      if (!quality || quality.level === 'unknown' || quality.dpi === null) {
        dpiEl.style.display = 'none';
        return;
      }

      if (quality.level === 'good' || quality.level === 'acceptable') {
        dpiEl.textContent = '✅ Отлично качество (' + quality.dpi + ' DPI)';
        dpiEl.className = 'pc-dpi pc-dpi--good';
      } else {
        dpiEl.textContent =
          '⚠️ Ниско качество на файла за печат (' + quality.dpi + ' DPI)';
        dpiEl.className = 'pc-dpi pc-dpi--warn';
      }

      dpiEl.style.display = 'block';
    }

    function resetUI() {
      operationVersion++;
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
        currentObjectUrl = null;
      }
      clearTimeout(resizeTimer);
      currentFileUuid = null;
      aspectRatio = null;
      maxMediaWidthCm = null;
      minPriceStotinki = 0;
      minQuantity = 1;
      unitPriceStotinki = 0;
      displayPrintProperties = null;
      sizeBounds = { minW: 1, maxW: 1000, minH: 1, maxH: 1000 };
      if (sizeEl) sizeEl.style.display = 'none';
      if (orderEl) orderEl.style.display = 'none';
      if (previewEl) previewEl.style.display = 'none';
      if (validationEl) validationEl.style.display = 'none';
      if (dpiEl) dpiEl.style.display = 'none';
      if (priceEl) priceEl.style.display = 'none';
      if (priceRowEl) priceRowEl.style.display = 'none';
      if (qtyInputBlock) qtyInputBlock.value = '1';
      if (qtyNoteEl) {
        qtyNoteEl.textContent = '';
        qtyNoteEl.classList.remove('pc-size-note--check');
      }
      setStatus(statusEl, '', false);
      clearPrintProperties(el);
      restoreThemePrices();
      showThemeQuantity();
    }
    
    function renderPriceDisplay() {
      if (!displayPrintProperties || !displayPrintProperties._print_price_stotinki) return;
      var stotinki = parseInt(displayPrintProperties._print_price_stotinki, 10);
      var qty = clampQty(getBlockQty());
      var w = displayPrintProperties._print_width_cm;
      var h = displayPrintProperties._print_height_cm;

      if (unitPriceEl) unitPriceEl.textContent = formatMoney(stotinki);
      if (totalPriceEl) totalPriceEl.textContent = formatMoney(stotinki * qty);
      if (qtyInputBlock) {
        qtyInputBlock.min = String(requiredMinQty());
        // While the buyer is mid-edit below the minimum, leave the field alone;
        // change/blur snaps it via clampQty.
        var typed = parseInt(qtyInputBlock.value, 10);
        if (!(isFinite(typed) && typed >= 1 && typed < requiredMinQty())) {
          if (String(qtyInputBlock.value) !== String(qty)) {
            qtyInputBlock.value = String(qty);
          }
        }
      }
      updateQtyHint();

      // Compact size line under the order controls (optional second display).
      if (priceEl) {
        priceEl.innerHTML =
          '<span class="pc-price-dims">' + w + ' × ' + h + ' см</span>';
      }

      // Theme product price shows the unit price; order total stays in the block.
      // Piece count is injected via _custom_quantity; the theme qty UI is hidden.
      updateThemePrices(stotinki);
    }

    // Theme quantity is hidden while a print file is attached; ignore its input.
  }

  // ── helpers ─────────────────────────────────────────────────────────────────

  /**
   * Dynamic checkout buttons ("Buy it now", Shop Pay, PayPal) send the buyer
   * straight to checkout without ever creating a cart, so line item properties
   * are never attached. A print order placed through one of them would carry no
   * file, no dimensions and no signed price, and would be charged the product's
   * catalogue price. They are hidden while a file is attached and restored the
   * moment it is removed.
   */
  var DYNAMIC_CHECKOUT_SELECTORS = [
    '.shopify-payment-button',
    '[data-shopify="payment-button"]',
  ];
  var hiddenDynamicButtons = [];

  function hideDynamicCheckoutButtons() {
    if (hiddenDynamicButtons.length > 0) return;
    DYNAMIC_CHECKOUT_SELECTORS.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (element) {
        hiddenDynamicButtons.push({ element: element, display: element.style.display });
        element.style.display = 'none';
      });
    });
  }

  function restoreDynamicCheckoutButtons() {
    hiddenDynamicButtons.forEach(function (entry) {
      entry.element.style.display = entry.display;
    });
    hiddenDynamicButtons = [];
  }

  function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  }

  function uploadWithProgress(url, file, onProgress) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.upload.onprogress = function (e) {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error('File upload failed (' + xhr.status + ')'));
      };
      xhr.onerror = function () { reject(new Error('Network error during upload')); };
      xhr.send(file);
    });
  }

  function get(url) {
    return nativeFetch(url, { method: 'GET' }).then(function (r) { return r.json(); });
  }

  function post(url, body) {
    return nativeFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw new Error(data.error || ('Request failed: ' + r.status));
        return data;
      });
    });
  }

  function enc(s) { return encodeURIComponent(s); }

  function setStatus(el, msg, isError, isLoading) {
    if (!document.getElementById('pc-file-activity-style')) {
      var style = document.createElement('style'); style.id = 'pc-file-activity-style';
      style.textContent = '.pc-file-loading{display:flex!important;align-items:center;gap:.8rem;overflow-wrap:anywhere}.pc-file-loading:before{content:"";display:block;flex:0 0 auto;width:1.6rem;height:1.6rem;border:.2rem solid currentColor;border-right-color:transparent;border-radius:50%;animation:pc-file-spin .8s linear infinite}@keyframes pc-file-spin{to{transform:rotate(360deg)}}@media(prefers-reduced-motion:reduce){.pc-file-loading:before{animation:none}}';
      document.head.appendChild(style);
    }
    el.textContent = msg;
    el.classList.toggle('pc-status--error', !!isError);
    el.classList.toggle('pc-file-loading', !!isLoading);
    var block = el.closest('.pc-block'); if (block) block.setAttribute('aria-busy', String(!!isLoading));
  }

  /**
   * Store print properties in module-level state + inject hidden inputs.
   *
   * Three-layer strategy ensures the properties reach the cart:
   *   1. Module variable → picked up by fetch/XHR interceptors (AJAX themes)
   *   2. Hidden inputs   → picked up by traditional form POST / FormData serialize
   *   3. Submit listener → last-resort injection right before submit
   *
   * The `_print_*` values come straight from the sign-price response and are
   * copied verbatim. They are the exact strings covered by the HMAC, so
   * reformatting any of them here would make the signature fail at checkout.
   */
  function setPrintProperties(blockEl, result) {
    var appUrl = (blockEl.dataset.appUrl || '').replace(/\/$/, '');
    var props = {};

    for (var signedKey in result.printAttributes) {
      if (Object.prototype.hasOwnProperty.call(result.printAttributes, signedKey)) {
        props[signedKey] = String(result.printAttributes[signedKey]);
      }
    }

    // Display-only extras, not covered by the signature.
    //
    // This used to be a "Print Price" property. The cart and the order already
    // show the line price, so it printed the same amount twice on the same row —
    // and in a different currency, while it was hardcoded to лв. What is
    // actually missing there is what was ordered, so the visible property now
    // carries the size.
    props['Размер'] =
      props._print_width_cm + ' × ' + props._print_height_cm + ' см';
    props['_Файл за печат'] =
      appUrl + '/api/download?uuid=' + encodeURIComponent(result.fileUuid);

    // Quantity is chosen in the block; interceptors honour `_custom_quantity`.
    var qtyField = blockEl.querySelector('[data-qty]');
    var qty = qtyField ? parseInt(qtyField.value, 10) : 1;
    if (!isFinite(qty) || qty < 1) qty = 1;
    props._custom_quantity = String(qty);

    if (blockEl.dataset.printCart === 'true') {
      blockEl.dispatchEvent(new CustomEvent('print:quote', { detail: props }));
      return;
    }

    hideDynamicCheckoutButtons();

    // Layer 1: module-level state for fetch/XHR interceptors
    activePrintProperties = props;

    // Layer 2: hidden inputs on the product form
    var form = findProductForm(blockEl);
    if (form) {
      for (var key in props) {
        if (
          Object.prototype.hasOwnProperty.call(props, key) &&
          key !== '_custom_variant_id' &&
          key !== '_custom_quantity'
        ) {
          setHidden(form, 'properties[' + key + ']', props[key]);
        }
      }
    }

    console.log('[print-calculator] properties set:', Object.keys(props).join(', '));
  }

  function clearPrintProperties(blockEl) {
    if (blockEl.dataset.printCart === 'true') {
      blockEl.dispatchEvent(new CustomEvent('print:quote', { detail: null }));
      return;
    }
    activePrintProperties = null;
    restoreDynamicCheckoutButtons();

    var form = findProductForm(blockEl);
    if (!form) return;
    ['_print_shop', '_print_product_id', '_print_file_uuid',
     '_print_width_cm', '_print_height_cm', '_print_price_stotinki',
     '_print_signed_at', '_print_price_signature', '_Файл за печат',
     'Размер', 'Print Price']
      .forEach(function (key) {
        var input = form.querySelector('[name="properties[' + key + ']"]');
        if (input) input.remove();
      });
  }

  function findProductForm(blockEl) {
    if (blockEl.dataset.printCart === 'true') return null;
    // Strategy 1: Walk up the DOM to find the nearest form with /cart/add action
    var el = blockEl;
    while (el) {
      if (el.tagName === 'FORM' && (el.action || '').includes('/cart/add')) return el;
      el = el.parentElement;
    }

    // Strategy 2: Find any product form on the page
    return (
      document.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('form[action="/cart/add"]') ||
      // Strategy 3: Shopify Dawn theme uses product-form element
      document.querySelector('product-form form') ||
      document.querySelector('form.product-form') ||
      document.querySelector('form.shopify-product-form')
    );
  }

  function setHidden(form, name, value) {
    var input = form.querySelector('[name="' + name + '"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }
})();
